import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, BarChart, Bar as RBar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/v2/lib/rbac";
import { GlassCard, Kpi, Chip, Skeleton, tip, AV_FALLBACK, PageHead } from "@/v2/admin/ui";
import {
  Users, UserCheck, CheckCircle2, Gauge, TrendingUp, Trophy, Sparkles, Activity, Coins,
  Flame, PlayCircle, AlertTriangle, Building2, ArrowUpRight, ArrowDownRight, Info, LayoutDashboard, LogIn, XCircle,
} from "lucide-react";

const TONE_ICON = { up: ArrowUpRight, down: ArrowDownRight, info: Info };

export default function Overview() {
  const { user } = useAuth();
  const canSeeInsights = can(user, "view_reports");
  const { data: s } = useQuery({ queryKey: ["adm-overview"], queryFn: async () => (await api.get("/admin/overview")).data });
  const { data: insights = [] } = useQuery({
    queryKey: ["adm-insights"],
    queryFn: async () => (await api.get("/admin/insights")).data,
    enabled: canSeeInsights,
  });

  if (!s) return <Skeleton className="h-96 w-full" />;
  const donut = [{ name: "ناجح", value: s.pass_rate }, { name: "لم يجتز", value: s.fail_rate }];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <PageHead icon={LayoutDashboard} title="مركز القيادة" sub="نظرة لحظية شاملة على أداء المنصة والموظفين." />

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
        <Kpi icon={Users} label="إجمالي الموظفين" value={s.total_employees} testId="kpi-employees" />
        <Kpi icon={UserCheck} label="نشطون اليوم" value={s.active_today} />
        <Kpi icon={LogIn} label="تسجيلات الدخول اليوم" value={s.logins_today} tone="gold" />
        <Kpi icon={CheckCircle2} label="تحديات مكتملة" value={s.completed_challenges} />
        <Kpi icon={Activity} label="محاولات الاختبار" value={s.attempts_count} tone="gold" />
        <Kpi icon={Gauge} label="متوسط الدرجات" value={s.avg_score} suffix="%" />
        <Kpi icon={Trophy} label="نسبة النجاح" value={s.pass_rate} suffix="%" />
        <Kpi icon={XCircle} label="نسبة الرسوب" value={s.fail_rate} suffix="%" tone="red" />
        <Kpi icon={TrendingUp} label="نسبة الإكمال" value={s.completion_rate} suffix="%" />
        <Kpi icon={PlayCircle} label="متوسط مشاهدة الفيديو" value={s.avg_watch_pct} suffix="%" tone="gold" />
        <Kpi icon={Coins} label="إجمالي النقاط" value={s.total_points} tone="gold" />
        <Kpi icon={Flame} label="متوسط سلسلة الحضور" value={s.avg_streak} tone="red" />
      </div>

      <GlassCard gold className="p-5" data-testid="admin-insights" style={canSeeInsights ? undefined : { display: "none" }}>
        <h3 className="font-bold mb-3 flex items-center gap-2"><Sparkles size={16} className="text-[#e3c799]" /> رؤى ذكية (AI Insights)</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {insights.map((ins, i) => {
            const I = TONE_ICON[ins.tone] || Info;
            const c = ins.tone === "up" ? "bg-[#91cab8]/15 text-[#91cab8]" : ins.tone === "down" ? "bg-[#B45309]/15 text-[#F7A08A]" : "bg-white/5 text-[#bcc8cf]";
            return (
              <div key={i} className="flex items-start gap-3 v2-inset p-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${c}`}><I size={16} /></div>
                <p className="text-sm text-[#f6f8fa]/85 leading-relaxed">{ins.text}</p>
              </div>
            );
          })}
          {insights.length === 0 && <p className="text-sm text-[#bcc8cf]">لا توجد رؤى كافية بعد.</p>}
        </div>
      </GlassCard>

      <div className="grid lg:grid-cols-3 gap-6">
        <GlassCard className="p-5 lg:col-span-2">
          <h3 className="font-bold mb-4">التفاعل خلال آخر 14 يومًا</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={s.series}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#91cab8" stopOpacity={0.5} /><stop offset="100%" stopColor="#91cab8" stopOpacity={0} /></linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#bb9661" stopOpacity={0.4} /><stop offset="100%" stopColor="#bb9661" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: "#bcc8cf", fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: "#bcc8cf", fontSize: 11 }} />
              <Tooltip {...tip} />
              <Area name="حضور" dataKey="checkins" stroke="#91cab8" strokeWidth={2} fill="url(#g1)" />
              <Area name="محاولات" dataKey="attempts" stroke="#bb9661" strokeWidth={2} fill="url(#g2)" />
            </AreaChart>
          </ResponsiveContainer>
        </GlassCard>

        <GlassCard className="p-5">
          <h3 className="font-bold mb-2">النجاح مقابل الرسوب</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={donut} dataKey="value" innerRadius={62} outerRadius={92} paddingAngle={3} stroke="none">
                <Cell fill="#91cab8" /><Cell fill="#B45309" />
              </Pie>
              <Tooltip {...tip} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#91cab8]" /> ناجح {s.pass_rate}%</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#B45309]" /> لم يجتز {s.fail_rate}%</span>
          </div>
        </GlassCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <GlassCard className="p-5">
          <h3 className="font-bold mb-4 flex items-center gap-2"><Building2 size={16} className="text-[#e3c799]" /> أداء الإدارات (متوسط النقاط)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={s.departments}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#bcc8cf", fontSize: 10 }} />
              <YAxis tick={{ fill: "#bcc8cf", fontSize: 11 }} />
              <Tooltip {...tip} />
              <RBar name="متوسط النقاط" dataKey="avg_points" fill="#bb9661" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>

        <div className="space-y-6">
          <GlassCard className="p-5">
            <h3 className="font-bold mb-3 flex items-center gap-2"><Trophy size={16} className="text-[#e3c799]" /> أفضل الموظفين</h3>
            <ul className="space-y-2">{s.top_employees.map((t, i) => (
              <li key={t.id} className="flex items-center gap-3">
                <span className="w-6 font-bold v2-gold-text">{i + 1}</span>
                <img src={t.avatar} alt={t.name} onError={AV_FALLBACK} className="w-9 h-9 rounded-full object-cover" />
                <span className="flex-1 font-semibold text-sm">{t.name}</span>
                <span className="font-extrabold v2-emerald-text">{t.points}</span>
              </li>
            ))}</ul>
          </GlassCard>
          <GlassCard className="p-5">
            <h3 className="font-bold mb-3 flex items-center gap-2"><AlertTriangle size={16} className="text-[#F7A08A]" /> يحتاجون إلى متابعة</h3>
            <ul className="space-y-2">{s.lagging_employees.map((t) => (
              <li key={t.id} className="flex items-center gap-3 text-sm">
                <span className="flex-1 font-semibold">{t.name}</span>
                <Chip tone="muted">{t.completed} تحدٍ</Chip>
                <span className="text-[#bcc8cf]">{t.points} نقطة</span>
              </li>
            ))}</ul>
          </GlassCard>
        </div>
      </div>
    </div>
  );
}
